import React, { createContext, useCallback, useState } from "react";
import { FormSchema, SectionDefinition } from "api/factories/form.factory";
import { FormProvider, useForm } from "react-hook-form";
import { useLazySchema, useYupValidationResolver } from "hooks";
import { RefetchOptions } from "react-query/types/core/query";
import { SectionDefinitionValue } from "components/SchemaProvider/FieldSelector/FieldSelector";

interface FormProviderProps {
    schema: FormSchema[];
    // eslint-disable-next-line
    refetch: (options?: RefetchOptions) => Promise<any>;
    actionFields: SectionDefinition[];
    isEdit: boolean;
    toggleEdit(): void;
    groupFields(
        schema: SectionDefinition<SectionDefinitionValue>[],
        groupLength?: number
    ): SectionDefinition<SectionDefinitionValue>[][];
}

export const SchemaProviderContext = createContext({} as FormProviderProps);

export const SchemaProvider: React.FC<{
    schema: FormSchema[];
    // eslint-disable-next-line
    refetch: (options?: RefetchOptions) => Promise<any>;
}> = ({ children, schema, refetch }) => {
    const [isEdit, setEdit] = useState(false);
    const formSchema = useLazySchema(schema);
    const methods = useForm({
        resolver: useYupValidationResolver(formSchema),
    });

    const groupFields = useCallback((sections: SectionDefinition<SectionDefinitionValue>[], groupLength = 2) => {
        return sections.reduce<SectionDefinition<SectionDefinitionValue>[][]>((fields, field) => {
            // value that is an array must be stored as group of 1 element
            if (Array.isArray(field.value)) {
                return fields.concat([[field]]);
            }

            if (!fields.length || (fields[fields.length - 1] && fields[fields.length - 1].length === groupLength)) {
                // group fields info provided length
                return fields.concat([[field]]);
            }

            const isSingleRow = fields[fields.length - 1].some((currentField) => Array.isArray(currentField.value));

            if (isSingleRow) {
                return fields.concat([[field]]);
            }

            fields[fields.length - 1].push(field);

            return fields;
        }, []);
    }, []);

    const handleEdit = useCallback(() => setEdit((isEdit) => !isEdit), []);

    const filterActionFields = useCallback((schema: FormSchema[]) => {
        return schema.reduce<SectionDefinition[]>((fields, { content }) => {
            return fields.concat(content.filter((field) => field.editable));
        }, []);
    }, []);

    return (
        <FormProvider {...methods}>
            <SchemaProviderContext.Provider
                value={{
                    schema,
                    refetch,
                    actionFields: filterActionFields(schema),
                    groupFields,
                    isEdit,
                    toggleEdit: handleEdit,
                }}>
                {children}
            </SchemaProviderContext.Provider>
        </FormProvider>
    );
};
